import { useEffect, useRef } from 'react';
import type { ChatMessage } from '@teo/widget-shared';
import {
  connectWidgetRealtime,
  detectReverbApiEnvMismatch,
  getWidgetSession,
  isValidWidgetThreadId,
  loadThreadMessages,
  logRealtime,
  mapPusherConnectionState,
  reportWidgetError,
  type RealtimeConnectionPhase,
  type ReverbClientConfig,
} from '@teo/widget-core';
import { useChat } from '../chat/ChatProvider.js';

const RETRY_DELAYS_MS = [1_000, 2_000, 5_000, 10_000, 30_000];

type UseWidgetRealtimeOptions = {
  apiBase?: string;
  reverbConfig?: ReverbClientConfig | null;
  threadId?: number | string | null;
  enabled?: boolean;
  onThreadIdChange?: (threadId: number) => void;
  onConnectionStateChange?: (phase: RealtimeConnectionPhase) => void;
};

async function refetchThreadMessages(
  apiBase: string,
  threadId: number | string,
  syncFromServer: (messages: ChatMessage[]) => void,
  setThreadMeta: (meta: { managerName?: string | null }) => void,
): Promise<void> {
  logRealtime('hook', 'refetch thread after reconnect', { threadId });
  const result = await loadThreadMessages(apiBase, threadId);
  syncFromServer(result.messages);
  setThreadMeta({ managerName: result.managerName });
}

/**
 * Subscribe to `NewSiteWidgetMessageEvent` and debounce-refetch thread history.
 * WS payload is never rendered directly — only merged server messages update UI.
 *
 * Backend requires an existing visitor/thread before `/broadcasting/auth` succeeds (403 otherwise).
 */
export function useWidgetRealtime({
  apiBase,
  reverbConfig,
  threadId = null,
  enabled = true,
  onThreadIdChange,
  onConnectionStateChange,
}: UseWidgetRealtimeOptions): void {
  const { syncFromServer, setThreadMeta } = useChat();
  const onThreadIdChangeRef = useRef(onThreadIdChange);
  const syncFromServerRef = useRef(syncFromServer);
  const setThreadMetaRef = useRef(setThreadMeta);
  const onConnectionStateChangeRef = useRef(onConnectionStateChange);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const retryAttemptRef = useRef(0);
  onThreadIdChangeRef.current = onThreadIdChange;
  syncFromServerRef.current = syncFromServer;
  setThreadMetaRef.current = setThreadMeta;
  onConnectionStateChangeRef.current = onConnectionStateChange;

  const emitConnectionPhase = (phase: RealtimeConnectionPhase) => {
    onConnectionStateChangeRef.current?.(phase);
  };

  useEffect(() => {
    if (!enabled || !apiBase || !reverbConfig) {
      if (enabled && apiBase && !reverbConfig) {
        logRealtime(
          'hook',
          'skipped: Reverb config missing (set data-reverb-key or bake VITE_REVERB_APP_KEY)',
          { apiBase },
          'warn',
        );
      }
      emitConnectionPhase('idle');
      return;
    }

    const envMismatch = detectReverbApiEnvMismatch(apiBase, reverbConfig.host);
    if (envMismatch) {
      logRealtime('hook', envMismatch, { apiBase, reverb: reverbConfig }, 'error');
    }

    if (!isValidWidgetThreadId(threadId)) {
      logRealtime(
        'hook',
        'waiting for thread_id before WebSocket connect (send first message or restore existing thread)',
        { threadId, apiBase, reverbHost: reverbConfig.host, envMismatch: envMismatch ?? undefined },
        'info',
      );
      emitConnectionPhase('idle');
      return;
    }

    const session = getWidgetSession();
    if (!session?.visitorId || !session.token) {
      logRealtime('hook', 'skipped: no session', { session }, 'warn');
      return;
    }

    logRealtime('hook', 'useWidgetRealtime mounting', {
      apiBase,
      enabled,
      visitorId: session.visitorId,
      threadId,
    });

    let disconnect: (() => void) | null = null;
    let cancelled = false;

    const scheduleRetry = () => {
      if (cancelled) return;
      const delay =
        RETRY_DELAYS_MS[
          Math.min(retryAttemptRef.current, RETRY_DELAYS_MS.length - 1)
        ];
      retryAttemptRef.current += 1;
      logRealtime('hook', 'scheduling reconnect', {
        attempt: retryAttemptRef.current,
        delayMs: delay,
      });
      retryTimerRef.current = setTimeout(() => {
        void connect();
      }, delay);
    };

    const connect = async () => {
      if (cancelled) return;
      emitConnectionPhase('connecting');

      try {
        const connection = await connectWidgetRealtime({
          apiBase,
          reverb: reverbConfig,
          visitorId: session.visitorId,
          callbacks: {
            onThreadIdUpdated: (nextThreadId) => {
              const numericId = Number(nextThreadId);
              if (Number.isFinite(numericId) && numericId > 0) {
                onThreadIdChangeRef.current?.(numericId);
              }
            },
            onThreadMessages: async (nextThreadId) => {
              logRealtime('hook', 'onThreadMessages → loadThreadMessages', {
                threadId: nextThreadId,
              });
              const result = await loadThreadMessages(apiBase, nextThreadId);
              if (cancelled) return;
              setThreadMetaRef.current({ managerName: result.managerName });
              syncFromServerRef.current(result.messages);
            },
            onConnectionStateChange: (current, previous) => {
              logRealtime('hook', 'connection state', { current, previous });
              emitConnectionPhase(mapPusherConnectionState(current));
            },
            onReconnected: () => {
              retryAttemptRef.current = 0;
              if (!isValidWidgetThreadId(threadId)) return;
              void refetchThreadMessages(
                apiBase,
                threadId,
                syncFromServerRef.current,
                setThreadMetaRef.current,
              ).catch((error) => {
                logRealtime('hook', 'refetch after reconnect failed', error, 'error');
                reportWidgetError(error, {
                  source: 'realtime',
                  fatal: false,
                });
              });
            },
          },
        });

        if (cancelled) {
          connection.disconnect();
          return;
        }

        disconnect = connection.disconnect;
        retryAttemptRef.current = 0;
        emitConnectionPhase('connected');
      } catch (error) {
        logRealtime('hook', 'realtime connection failed', error, 'error');
        console.warn('[Teo Widget] Realtime connection failed', error);
        reportWidgetError(error, {
          source: 'realtime',
          fatal: false,
        });
        emitConnectionPhase('disconnected');
        scheduleRetry();
      }
    };

    void connect();

    return () => {
      logRealtime('hook', 'useWidgetRealtime cleanup');
      cancelled = true;
      emitConnectionPhase('idle');
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
      }
      disconnect?.();
    };
  }, [apiBase, reverbConfig, enabled, threadId, syncFromServer]);
}
